import { type CSSResultGroup, html, LitElement, nothing, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { computePosition, flip, offset, shift } from '@floating-ui/dom'
import { isEscape } from '@/navbar/utils/eventKeys'
import type { Icon } from '@/navbar/navbar.types'

// Styles:
import styles from './eco-suite-navbar-tooltip.scss?inline'

/**
 * @internal
 */
@customElement('eco-suite-navbar-tooltip')
export class EcoSuiteNavbarTooltip extends LitElement {
  static readonly styles: CSSResultGroup = [unsafeCSS(styles)]
  private static readonly TOOLTIP_ID = 'content'

  private showTimeout?: ReturnType<typeof setTimeout>
  private hideTimeout?: ReturnType<typeof setTimeout>
  private autoHideTimeout?: ReturnType<typeof setTimeout>

  @query('.EcoSuiteNavbarTooltip')
  private readonly tooltipEl!: HTMLDivElement

  @property()
  content?: string

  @property()
  icon?: Icon

  @state()
  private visible = false

  disconnectedCallback() {
    super.disconnectedCallback()
    clearTimeout(this.showTimeout)
    clearTimeout(this.hideTimeout)
    clearTimeout(this.autoHideTimeout)
    this.visible = false
  }

  private show() {
    clearTimeout(this.hideTimeout)
    if (this.visible || !this.content) return
    this.showTimeout = setTimeout(() => {
      this.visible = true
      this.autoHideTimeout = setTimeout(() => this.hide(), 5000)
      this.updateComplete.then(() => this.positionTooltip())
    }, 200)
  }

  private hide() {
    clearTimeout(this.showTimeout)
    clearTimeout(this.autoHideTimeout)
    this.hideTimeout = setTimeout(() => {
      this.visible = false
    }, 100)
  }

  private handleKeyUp(e: KeyboardEvent) {
    if (isEscape(e) || e.metaKey || e.ctrlKey || e.altKey || e.key === 'Shift') return
    this.show()
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (isEscape(e) && this.visible) {
      this.hide()
    }
  }

  private handleFocusOut(e: FocusEvent) {
    // Only hide if focus has moved outside this component's subtree
    if (this.contains(e.relatedTarget as Node | null)) return
    this.hide()
  }

  private async positionTooltip() {
    if (!this.tooltipEl) return

    const { x, y } = await computePosition(this, this.tooltipEl, {
      placement: 'bottom',
      middleware: [
        offset(6),
        flip(),
        shift({ padding: 8 }),
      ],
    })

    Object.assign(this.tooltipEl.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  render() {
    const tooltipId = EcoSuiteNavbarTooltip.TOOLTIP_ID
    return html`
      <slot
        aria-describedby=${this.content ? tooltipId : nothing}
        @mouseenter=${this.show}
        @mouseleave=${this.hide}
        @mousedown=${this.hide}
        @focusout=${this.handleFocusOut}
        @keyup=${this.handleKeyUp}
        @keydown=${this.handleKeyDown}
      ></slot>
      ${this.content
        ? html`
          <div
            id=${tooltipId}
            class=${classMap({
              EcoSuiteNavbarTooltip: true,
              'EcoSuiteNavbarTooltip--visible': this.visible,
              'EcoSuiteNavbarTooltip--hasIcon': !!this.icon,
              'EcoSuiteNavbarTooltip--hasChevronIcon': this.icon === 'chevron-right',
              'EcoSuiteNavbarTooltip--hasExternalLinkIcon': this.icon === 'external-link',
            })}
            role="tooltip"
            aria-hidden=${this.visible ? nothing : 'true'}
          >
            <span>${this.content}</span>
            ${this.icon ? html`<pwc-icon-shadow icon=${ifDefined(this.icon)} size="custom" aria-hidden="true"></pwc-icon-shadow>` : nothing}
          </div>
        `
        : nothing
      }
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-tooltip': EcoSuiteNavbarTooltip
  }
}
